import React, { useState, useEffect } from 'react';
import { getFirestore, collection, getDocs, query, where } from 'firebase/firestore';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../Logout';
import MultipleChoiceQuestion from './MultipleChoiceQuestion';
import TrueFalseQuestion from './TrueFalseQuestion';
import { ProgressBar, Button, Card } from 'react-bootstrap';
import { FaChartBar } from 'react-icons/fa';

const MAX_INCORRECT = 5;

const QuizPage = () => {
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [incorrectCount, setIncorrectCount] = useState(0);
  const [showProSubscription, setShowProSubscription] = useState(false);
  const location = useLocation();
  const { selectedCourse, lessonId } = location.state || {};
  const navigate = useNavigate();

  useEffect(() => {
    document.body.style.backgroundColor = '#000';
    document.body.style.color = '#fff';
    return () => {
      document.body.style.backgroundColor = '';
      document.body.style.color = '';
    };
  }, []);

  useEffect(() => {
    let incorrectAnswers = parseInt(localStorage.getItem('incorrectAnswerCount')) || 0;
    let lastAttemptTimestamp = parseInt(localStorage.getItem('lastAttemptTimestamp')) || 0;
    let currentTimestamp = Date.now();

    if (lastAttemptTimestamp && (currentTimestamp - lastAttemptTimestamp > 24 * 60 * 60 * 1000)) {
      localStorage.setItem('incorrectAnswerCount', '0');
      incorrectAnswers = 0;
    }

    setIncorrectCount(incorrectAnswers);

    if (incorrectAnswers >= MAX_INCORRECT) {
      setShowProSubscription(true);
    }
  }, []);

  useEffect(() => {
    if (!lessonId) {
      console.error("❌ No lessonId received! Check navigation in WelcomePage.");
      return;
    }

    const fetchQuestions = async () => {
      const db = getFirestore();
      try {
        const q = query(
          collection(db, 'questions_new'),
          where('lessonid', '==', lessonId),
          where('lessontypeid', 'in', ['1', '2'])
        );
        const snapshot = await getDocs(q);
        const lessonQuestions = snapshot.docs.map((doc) => doc.data());
        setQuestions(lessonQuestions);
      } catch (error) {
        console.error("❌ Error fetching questions:", error);
      }
    };

    fetchQuestions();
  }, [lessonId]);

  const handleAnswerResult = async (isAnswerCorrect) => {
    if (!isAnswerCorrect) {
      let incorrectAnswers = incorrectCount + 1;
      localStorage.setItem('incorrectAnswerCount', incorrectAnswers);
      localStorage.setItem('lastAttemptTimestamp', Date.now().toString());
      setIncorrectCount(incorrectAnswers);

      if (incorrectAnswers >= MAX_INCORRECT && !showProSubscription) {
        setShowProSubscription(true);
        return;
      }
    }
    handleNext();
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      alert('Congratulations! You have finished the quiz! 🎉');
      navigate('/welcome', { state: { showLessonsDirectly: true, selectedCourse } });
    }
  };

  const handleCloseProSubscription = () => {
    setShowProSubscription(false);
  };

  const currentQuestion = questions[currentIndex];

  return (
    <Layout>
      {/* ✅ Progress Bar for Incorrect Answers */}
      <div style={{
        width: '100vw',
        backgroundColor: '#000',
        padding: '10px 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          width: '50%',
        }}>
          <ProgressBar
            now={((currentIndex + 1) / questions.length) * 100}
            variant="success"
            style={{ width: '40%', height: '6px', borderRadius: '5px', backgroundColor: '#000' }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <FaChartBar style={{ color: '#2ECC71', fontSize: '24px' }} />
            <span style={{ color: '#2ECC71', fontSize: '18px', fontWeight: 'bold' }}>
              {MAX_INCORRECT - incorrectCount}
            </span>
          </div>
        </div>
      </div>

      {/* Quiz Content */}
      <div style={{
        fontFamily: 'Nunito Sans',
        minHeight: '100vh',
        padding: '20px',
        position: 'relative',
        backgroundColor: '#000',
        color: '#fff'
      }}>
        {currentQuestion ? (
          <>
            {currentQuestion.questiontypeid === '1' && (
              <MultipleChoiceQuestion
                question={currentQuestion}
                onNext={handleAnswerResult}
              />
            )}
            {currentQuestion.questiontypeid === '2' && (
              <TrueFalseQuestion
                question={currentQuestion}
                onNext={handleAnswerResult}
              />
            )}
          </>
        ) : (
          <p>Loading questions...</p>
        )}
      </div>

      {/* ✅ Pro Subscription Overlay */}
      {showProSubscription && (
        <div style={{
          position: 'fixed',
          top: '0',
          left: '0',
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.7)', // ✅ Dark overlay for focus
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: '9999'
        }}>
          <Card className="p-4 text-center"
            style={{
              backgroundColor: '#ffffff', // ✅ Light background for contrast
              color: '#232830', // ✅ Dark text
              width: '90%',
              maxWidth: '500px',
              borderRadius: '15px',
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
              position: 'relative'
            }}>

            {/* ❌ Close Button */}
            <button 
              onClick={handleCloseProSubscription} 
              style={{ 
                position: 'absolute', 
                top: '10px', 
                right: '10px', 
                border: 'none', 
                background: 'none', 
                fontSize: '18px', 
                cursor: 'pointer', 
                color: '#FF0000'
              }}>
              ❌
            </button>

            <h2 style={{ fontWeight: 'bold' }}>Upgrade to FinTorro Pro</h2>
            <p>Achieve your trading and investing goals powered by AI</p>
            <div>⭐⭐⭐⭐⭐</div>
            <p style={{ fontStyle: 'italic', fontWeight: 'bold' }}>“Helped me do a 180° transition!”</p>
            <p>Prior to FinTorro, I avoided the markets. - Jovin</p>
            <p>Cancel anytime | No Fees or Penalties</p>

            {/* ✅ Pricing & Join Now Button */}
            <h4 style={{ fontWeight: 'bold', color: '#FF7C24' }}>£59.99/yr (£4.99/mo)</h4>
            <Button
              style={{
                backgroundColor: '#FF7C24',
                borderColor: '#FF7C24',
                width: '100%',
                fontSize: '18px',
                fontWeight: 'bold',
                padding: '12px',
                borderRadius: '10px'
              }}
              onClick={() => window.open("https://buy.stripe.com/eVa29y1Ig8zsgFycMZ", "_blank")}
            >
              Join Now
            </Button>
          </Card>
        </div>
      )}
    </Layout>
  );
};

export default QuizPage;




// import React, { useState, useEffect } from 'react';
// import { getFirestore, collection, getDocs, query, where } from 'firebase/firestore';
// import { useLocation, useNavigate } from 'react-router-dom';
// import Layout from '../Logout';
// import MultipleChoiceQuestion from './MultipleChoiceQuestion';
// import TrueFalseQuestion from './TrueFalseQuestion';
// import { ProgressBar, Button, Card } from 'react-bootstrap';
// import { FaChartBar } from 'react-icons/fa';

// const MAX_INCORRECT = 5;

// const QuizPage = () => {
//   const [questions, setQuestions] = useState([]);
//   const [currentIndex, setCurrentIndex] = useState(0);
//   const [incorrectCount, setIncorrectCount] = useState(0);
//   const [showProSubscription, setShowProSubscription] = useState(false); // ✅ Controls Pro Subscription UI
//   const location = useLocation();
//   const { selectedCourse, lessonId } = location.state || {};
//   const navigate = useNavigate();


//    // ✅ Add this useEffect here (after useState declarations)
//    useEffect(() => {
//     document.body.style.backgroundColor = '#000'; // ✅ Make entire background black
//     document.body.style.color = '#fff'; // ✅ Ensure text remains readable

//     return () => {
//       document.body.style.backgroundColor = ''; // ✅ Reset when component unmounts
//       document.body.style.color = '';
//     };
//   }, []);

//   // ✅ Load incorrect answer count from localStorage on mount
//   useEffect(() => {
//     let incorrectAnswers = parseInt(localStorage.getItem('incorrectAnswerCount')) || 0;
//     let lastAttemptTimestamp = parseInt(localStorage.getItem('lastAttemptTimestamp')) || 0;
//     let currentTimestamp = Date.now();

//     // Reset incorrect count if 24 hours have passed
//     if (lastAttemptTimestamp && (currentTimestamp - lastAttemptTimestamp > 24 * 60 * 60 * 1000)) {
//       localStorage.setItem('incorrectAnswerCount', '0');
//       incorrectAnswers = 0;
//     }

//     setIncorrectCount(incorrectAnswers);

//     // ✅ Show Pro Subscription UI only if the incorrect limit is reached
//     if (incorrectAnswers >= MAX_INCORRECT) {
//       setShowProSubscription(true);
//     }
//   }, []); // ✅ Removed `showProSubscription` from dependencies to prevent re-triggering

//   useEffect(() => {
//     if (!lessonId) {
//       console.error("❌ No lessonId received! Check navigation in WelcomePage.");
//       return;
//     }

//     const fetchQuestions = async () => {
//       const db = getFirestore();
//       try {
//         const q = query(
//           collection(db, 'questions_new'),
//           where('lessonid', '==', lessonId),
//           where('lessontypeid', 'in', ['1', '2'])
//         );
//         const snapshot = await getDocs(q);
//         const lessonQuestions = snapshot.docs.map((doc) => doc.data());
//         setQuestions(lessonQuestions);
//       } catch (error) {
//         console.error("❌ Error fetching questions:", error);
//       }
//     };

//     fetchQuestions();
//   }, [lessonId]);

//   // ✅ Handle answer result
//   const handleAnswerResult = async (isAnswerCorrect) => {
//     if (!isAnswerCorrect) {
//       let incorrectAnswers = incorrectCount + 1;
//       localStorage.setItem('incorrectAnswerCount', incorrectAnswers);
//       localStorage.setItem('lastAttemptTimestamp', Date.now().toString());
//       setIncorrectCount(incorrectAnswers);

//       // ✅ Show Pro Subscription UI only if it has not been displayed already
//       if (incorrectAnswers >= MAX_INCORRECT && !showProSubscription) {
//         setShowProSubscription(true);
//         return;
//       }
//     }
//     handleNext();
//   };

//   // ✅ Move to next question or finish the quiz
//   const handleNext = () => {
//     if (currentIndex < questions.length - 1) {
//       setCurrentIndex(currentIndex + 1);
//     } else {
//       alert('Congratulations! You have finished the quiz! 🎉');
      
//       // ✅ Navigate back to the WelcomePage (UI Screen)
//       navigate('/welcome', { state: { showLessonsDirectly: true, selectedCourse } });
//     }
//   };

//   // ✅ Close the Pro Subscription UI properly
//   const handleCloseProSubscription = () => {
//     setShowProSubscription(false);
//   };

//   const currentQuestion = questions[currentIndex];

//   return (
//     <Layout>
//      {/* ✅ Progress Bar for Incorrect Answers */}
//      <div style={{ 
//   width: '100vw', // ✅ Make it span the full viewport width
//   backgroundColor: '#000', // ✅ Force the entire background to black
//   padding: '10px 0', // ✅ Add padding to prevent cut-off
//   display: 'flex', 
//   alignItems: 'center', 
//   justifyContent: 'center',
//   position: 'relative' // ✅ Ensure it stays above other elements
// }}>
//   <div style={{ 
//     display: 'flex', 
//     alignItems: 'center', 
//     justifyContent: 'center', 
//     gap: '8px',
//     width: '50%', // ✅ Ensures progress bar stays centered
//   }}>
//     <ProgressBar
//   now={((currentIndex + 1) / questions.length) * 100} // ✅ Dynamically update based on quiz progress
//   variant="success"
//   style={{ width: '40%', height: '6px', borderRadius: '5px', backgroundColor: '#000' }} // ✅ Keep black background
// />

//     <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
//       <FaChartBar style={{ color: '#2ECC71', fontSize: '24px' }} />
//       <span style={{ color: '#2ECC71', fontSize: '18px', fontWeight: 'bold' }}>
//         {MAX_INCORRECT - incorrectCount}
//       </span>
//     </div>
//   </div>
// </div>


//       {/* Quiz Content */}
//       <div style={{
//   fontFamily: 'Nunito Sans',
//   minHeight: '100vh',
//   padding: '20px',
//   position: 'relative',
//   backgroundColor: '#000',  // ✅ Set background to black
//   color: '#fff'  // ✅ Ensure text remains readable
// }}>
//         {currentQuestion ? (
//           <>
//             {currentQuestion.questiontypeid === '1' && (
//               <MultipleChoiceQuestion
//                 question={currentQuestion}
//                 onNext={handleAnswerResult}
//               />
//             )}
//             {currentQuestion.questiontypeid === '2' && (
//               <TrueFalseQuestion
//                 question={currentQuestion}
//                 onNext={handleAnswerResult}
//               />
//             )}
//           </>
//         ) : (
//           <p>Loading questions...</p>
//         )}
//       </div>

//       {/* ✅ Pro Subscription Overlay - Shows Over Quiz */}
//       {showProSubscription && (
//         <div style={{
//           position: 'absolute',
//           top: '0',
//           left: '0',
//           width: '100%',
//           height: '100%',
//           backgroundColor: 'rgba(0, 0, 0, 0.8)', // ✅ Dark overlay behind the UI
//           display: 'flex',
//           justifyContent: 'center',
//           alignItems: 'center',
//           zIndex: '9999'
//         }}>
//           <Card className="p-4 mx-auto text-center"
//             style={{ backgroundColor: '#E0F7FA', width: '90%', maxWidth: '500px', borderRadius: '15px', position: 'relative' }}>
            
//             {/* ❌ Close Button */}
//             <button 
//               onClick={handleCloseProSubscription} 
//               style={{ 
//                 position: 'absolute', 
//                 top: '10px', 
//                 right: '10px', 
//                 border: 'none', 
//                 background: 'none', 
//                 fontSize: '18px', 
//                 cursor: 'pointer', 
//                 color: '#FF0000' 
//               }}>
//               ❌
//             </button>

//             <h2 style={{ fontWeight: 'bold' }}>Upgrade to FinTorro Pro</h2>
//             <p>Achieve your trading and investing goals powered by AI</p>

//             {/* ⭐ Star Ratings */}
//             <div>⭐⭐⭐⭐⭐</div>

//             <p style={{ fontStyle: 'italic', fontWeight: 'bold' }}>“Helped me do a 180° transition!”</p>
//             <p>Prior to FinTorro, I avoided the markets. - Jovin</p>

//             <p>Cancel anytime | No Fees or Penalties</p>
//                {/* ✅ Features */}
//         <div className="p-3" style={{ backgroundColor: '#B2EBF2', borderRadius: '10px' }}>
//           <table className="table">
//             <tbody>
//               <tr><td>Daily Lessons</td> <td>✅</td></tr>
//               <tr><td>Unlimited Courses</td> <td>✅</td></tr>
//               <tr><td>Unlimited Practice</td> <td>✅</td></tr>
//               <tr><td>AI Learning</td> <td>✅</td></tr>
//               <tr><td>10,000 Lessons</td> <td>✅</td></tr>
//               <tr><td>Join Challenges</td> <td>✅</td></tr>
//               <tr><td>Includes Crypto, Forex, Psychology</td> <td>✅</td></tr>
//             </tbody>
//           </table>
//         </div>
//             {/* ✅ Pricing & Join Now Button */}
//             <h4>£59.99/yr (£4.99/mo)</h4>
//             <Button style={{ backgroundColor: '#FF7C24', borderColor: '#FF7C24', width: '100%' }}>Join Now</Button>
//           </Card>
//         </div>
//       )}
//     </Layout>
//   );
// };

// export default QuizPage;
